import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to intelligently combine class names using `clsx`
 * and resolve Tailwind CSS class conflicts using `tailwind-merge`.
 *
 * This function is commonly used in React or other frontend frameworks
 * to conditionally merge Tailwind classes and avoid class duplication or conflict,
 * especially when combining utility variants like `bg-red-500` and `bg-blue-500`.
 *
 * @param {...ClassValue[]} args - A variadic list of class values. Each can be:
 *  - string: e.g. `"px-4 py-2"`
 *  - object: e.g. `{ 'text-red-500': isError }`
 *  - array: e.g. `['bg-blue-500', conditionalClass]`
 *  These will be normalized and merged by `clsx`, then conflict-resolved by `tailwind-merge`.
 *
 * @returns {string} A single string of valid Tailwind class names with conflicts resolved.
 *
 * @example
 * ```tsx
 * <button className={cn("px-4 py-2", isPrimary && "bg-blue-500", "bg-red-500")} />
 * // Result: "px-4 py-2 bg-blue-500" (if isPrimary is true)
 * ```
 *
 * @see https://github.com/lukeed/clsx - `clsx` docs for conditional class logic
 * @see https://github.com/dcastil/tailwind-merge - `tailwind-merge` docs for conflict resolution
 */
export default function cn(...args: ClassValue[]): string {
  return twMerge(clsx(args));
}
